// shell/components/entity-view/entity-view.ts

import { IDisposable } from '../types';
import { DisposableStore } from '../disposable';
import { createElement, clearElement } from '../../utils/dom';
import type {
    EntityViewConfig,
    EntityViewMode,
    FormConfig,
    FormFieldDef,
    ViewFieldDef,
    CustomFormFieldController,
} from './types';
import { EntityLayout, type FieldRenderContext } from '../entity-layout/entity-layout';
import { renderViewField, renderFormField, type ViewRenderResult } from '../entity-fields/field-registry';
import type { FormRendererResult } from '../entity-fields/form-renderers/types';

// ============================================================================
// EntityView Class
// ============================================================================

/**
 * EntityView is the main component for displaying and editing entity data.
 *
 * It manages:
 * - Mode switching between view/edit/new
 * - Model loading with loading/error states
 * - Form state (dirty, valid, errors)
 * - CRUD operations
 *
 * @template T - The entity model type
 */
export class EntityView<T> implements IDisposable {
    private readonly config: EntityViewConfig<T>;
    private readonly disposables = new DisposableStore();

    // State
    private _mode: EntityViewMode;
    private _model: T | null = null;
    private _originalModel: T | null = null;
    private _isLoading = false;
    private _loadError: Error | null = null;
    private _errors = new Map<string, string>();
    private _fieldControllers = new Map<string, CustomFormFieldController>();
    private _disposed = false;

    // DOM elements
    private readonly rootElement: HTMLElement;

    // View mode components
    private viewLayout: EntityLayout<T> | null = null;
    private viewFieldResults = new Map<string, ViewRenderResult>();

    // Form mode components
    private formLayout: EntityLayout<T> | null = null;
    private formFieldResults = new Map<string, FormRendererResult>();

    constructor(config: EntityViewConfig<T>) {
        this.config = config;
        this._mode = config.initialMode;

        // Create root container
        this.rootElement = createElement('div', {
            className: 'ev-container',
            attributes: {
                'role': this._mode === 'view' ? 'region' : 'form',
                'aria-label': config.ariaLabel,
            },
        });
        this.rootElement.classList.add(`ev-mode-${this._mode}`);

        config.container.appendChild(this.rootElement);

        // Load model (for both edit and new modes - new mode can have default values)
        this.loadModel();
    }

    // =========================================================================
    // Public Getters
    // =========================================================================

    /** Current operating mode */
    get mode(): EntityViewMode {
        return this._mode;
    }

    /** Current model data */
    get model(): T | null {
        return this._model;
    }

    /** Whether form has unsaved changes */
    get isDirty(): boolean {
        if (this._mode === 'view' || !this._model || !this._originalModel) {
            return false;
        }
        return !this.isModelEqual(this._model, this._originalModel);
    }

    /** Whether form passes validation */
    get isValid(): boolean {
        return this._errors.size === 0;
    }

    /** Whether model is currently loading */
    get isLoading(): boolean {
        return this._isLoading;
    }

    /** Error from loading, if any */
    get loadError(): Error | null {
        return this._loadError;
    }

    /** Current validation errors by field key */
    get errors(): ReadonlyMap<string, string> {
        return this._errors;
    }

    // =========================================================================
    // Mode Switching
    // =========================================================================

    /**
     * Switch to a different mode.
     * If dirty and confirmDirtyState is enabled, prompts user first.
     */
    async setMode(mode: EntityViewMode): Promise<boolean> {
        if (mode === this._mode) {
            return true;
        }

        // Reject edit/new when no form is configured
        if ((mode === 'edit' || mode === 'new') && !this.config.form && !this.config.newForm) {
            return false;
        }

        // Check for unsaved changes
        if (this.isDirty && this.config.confirmDirtyState !== false) {
            const confirmed = await this.confirmDirtyState();
            if (!confirmed) {
                return false;
            }
        }

        const previousMode = this._mode;
        this._mode = mode;

        // Update root element
        this.rootElement.classList.remove(`ev-mode-${previousMode}`);
        this.rootElement.classList.add(`ev-mode-${mode}`);
        this.rootElement.setAttribute('role', mode === 'view' ? 'region' : 'form');

        // Reset form state when entering edit/new
        if (mode === 'edit' || mode === 'new') {
            this._errors.clear();
            if (mode === 'new') {
                this._model = {} as T;
                this._originalModel = {} as T;
            } else {
                // Entering edit mode - snapshot current model
                this._originalModel = this.cloneModel(this._model!);
            }
        }

        // Re-render for new mode
        this.render();

        // Notify listeners
        this.config.onModeChange?.(mode);

        // Focus first field in form mode
        if (mode === 'edit' || mode === 'new') {
            this.focusFirstField();
        }

        return true;
    }

    // =========================================================================
    // Model Loading
    // =========================================================================

    /**
     * Load or reload the model from the data source.
     */
    async reload(): Promise<void> {
        await this.loadModel();
    }

    private async loadModel(): Promise<void> {
        this._isLoading = true;
        this._loadError = null;
        this.renderLoadingState();

        try {
            this._model = await this.config.getModel();
            this._originalModel = this.cloneModel(this._model);
            await this.config.afterLoad?.();
            this.render();
        } catch (error) {
            this._loadError = error instanceof Error ? error : new Error(String(error));
            this.renderErrorState();
        } finally {
            this._isLoading = false;
        }
    }

    // =========================================================================
    // Form Operations
    // =========================================================================

    /**
     * Validate all fields and save if valid.
     * @returns true if save succeeded, false otherwise
     */
    async save(): Promise<boolean> {
        if (this._mode === 'view') {
            return false;
        }

        const valid = await this.validate();
        if (!valid) {
            this.focusFirstError();
            return false;
        }

        try {
            const wasNew = this._mode === 'new';
            if (wasNew) {
                await this.config.onCreate?.(this._model!);
            } else {
                await this.config.onUpdate?.(this._model!);
            }

            // Callback may have closed the pane, disposing this view
            if (this._disposed) return true;

            // Switch to view mode and reload the full model from the server.
            // Server-side effects (timestamps, computed fields, validation
            // invalidation) are not reflected in the in-memory model.
            const previousMode = this._mode;
            this.rootElement.classList.remove(`ev-mode-${previousMode}`);
            this.rootElement.classList.add('ev-mode-view');
            this.rootElement.setAttribute('role', 'region');
            this._mode = 'view';
            this.config.onModeChange?.('view');
            await this.loadModel();
            return true;
        } catch (error) {
            // Add a general error
            this._errors.set('_form', error instanceof Error ? error.message : 'Save failed');
            this.renderErrors();
            return false;
        }
    }

    /**
     * Cancel editing and revert to original model.
     * If onCancel callback is provided, calls it instead of switching to view mode.
     */
    cancel(): void {
        if (this._mode === 'view') {
            return;
        }

        // If custom onCancel handler is provided, use it (e.g., to close pane)
        if (this.config.onCancel) {
            this.config.onCancel();
            return;
        }

        // In new mode there is no existing entity to revert to — call
        // onNewCancel if provided, otherwise fall through to view mode.
        if (this._mode === 'new' && this.config.onNewCancel) {
            this.config.onNewCancel();
            return;
        }

        // Revert to original model
        if (this._originalModel) {
            this._model = this.cloneModel(this._originalModel);
        }

        this._errors.clear();

        // Switch back to view mode (skip dirty check since we're reverting)
        const previousMode = this._mode;
        this._mode = 'view';

        this.rootElement.classList.remove(`ev-mode-${previousMode}`);
        this.rootElement.classList.add('ev-mode-view');
        this.rootElement.setAttribute('role', 'region');

        this.render();
        this.config.onModeChange?.('view');
    }

    /**
     * Validate all form fields.
     * @returns true if all fields are valid
     */
    async validate(): Promise<boolean> {
        if (this._mode === 'view') {
            return true;
        }

        this._errors.clear();

        const formConfig = this.getFormConfig();
        const validationPromises: Promise<void>[] = [];

        for (const field of formConfig.fields) {
            validationPromises.push(this.validateField(field));
        }

        await Promise.all(validationPromises);

        this.renderErrors();
        return this._errors.size === 0;
    }

    // =========================================================================
    // Field Access
    // =========================================================================

    /**
     * Get a field value from the model.
     */
    getFieldValue<V>(key: string): V | undefined {
        if (!this._model) return undefined;
        return (this._model as Record<string, unknown>)[key] as V;
    }

    /**
     * Set a field value on the model.
     */
    setFieldValue<V>(key: string, value: V): void {
        if (!this._model) return;
        (this._model as Record<string, unknown>)[key] = value;
        // Clear error for this field when value changes
        this._errors.delete(key);
        // Update Save button state
        this.updateSaveButtonState();
        // Re-evaluate field visibility (fields may depend on each other)
        this.reevaluateVisibility();
        // Notify consumer
        this.config.onFieldChange?.(key, value);
    }

    /** Re-evaluate `visible` predicates on all form fields and show/hide accordingly. */
    private reevaluateVisibility(): void {
        if (!this._model || !this.formLayout) return;
        for (const field of this.getFormConfig().fields) {
            if (!field.visible) continue;
            const container = this.formLayout.getFieldContainer(String(field.key));
            if (container) {
                container.style.display = field.visible(this._model) ? '' : 'none';
            }
        }
        this.formLayout.refreshRowSizes();
    }

    /**
     * Re-render the form inputs from the current draft model (edit/new only).
     * `setFieldValue` mutates the model but does not re-render built-in field
     * inputs, so call this after a programmatic value change the user should see
     * (e.g. staging a value into a tag-input). No-op in view mode.
     */
    refreshForm(): void {
        if ((this._mode === 'edit' || this._mode === 'new') && this._model) {
            this.formLayout?.render(this._model);
        }
    }


    /**
     * Focus the view (first focusable element).
     */
    focus(): void {
        if (this._mode === 'view') {
            this.rootElement.focus();
        } else {
            this.focusFirstField();
        }
    }

    // =========================================================================
    // IDisposable
    // =========================================================================

    dispose(): void {
        this._disposed = true;

        // Dispose view layout
        this.disposeViewLayout();

        // Dispose form layout
        this.disposeFormLayout();

        // Dispose field controllers
        for (const controller of this._fieldControllers.values()) {
            controller.dispose?.();
        }
        this._fieldControllers.clear();

        // Clear DOM
        clearElement(this.rootElement);
        this.rootElement.remove();

        // Dispose tracked resources
        this.disposables.dispose();
    }

    // =========================================================================
    // Private: Rendering
    // =========================================================================

    private render(): void {
        // Dispose existing field controllers
        for (const controller of this._fieldControllers.values()) {
            controller.dispose?.();
        }
        this._fieldControllers.clear();

        clearElement(this.rootElement);

        if (this._mode === 'view') {
            this.renderViewMode();
        } else {
            this.renderFormMode();
        }
    }

    private renderViewMode(): void {
        // Dispose previous view layout
        this.disposeViewLayout();

        // In-pane view header (e.g. an "Edit" button) — mirrors the form header
        // so a display-only view can become editable without a global action
        // bar. The EntityLayout below mounts its own `el-layout` child, so this
        // header (a direct child of rootElement) survives the layout's clear.
        const viewActions = this.config.viewActions?.() ?? [];
        if (viewActions.length > 0) {
            const header = createElement('div', { className: 'ev-form-header' });
            const actions = createElement('div', { className: 'ev-form-actions' });
            for (const btn of viewActions) actions.appendChild(btn);
            header.appendChild(actions);
            this.rootElement.appendChild(header);
        }

        // Build field lookup map from config
        const fieldMap = new Map<string, ViewFieldDef<T>>();
        for (const field of this.config.view.fields) {
            fieldMap.set(String(field.key), field);
        }

        // Create EntityLayout with field renderer
        this.viewLayout = new EntityLayout<T>({
            container: this.rootElement,
            layout: this.config.view.layout,
            ariaLabel: this.config.ariaLabel,
            renderField: (ctx: FieldRenderContext<T>) => {
                const fieldDef = fieldMap.get(ctx.key);
                if (!fieldDef) {
                    // Field not defined - render placeholder
                    const label = createElement('div', {
                        className: 'ev-field-label',
                        textContent: ctx.key,
                    });
                    ctx.container.appendChild(label);

                    const value = createElement('div', {
                        className: 'ev-field-value ev-field-value--unknown',
                        textContent: String(ctx.value ?? '—'),
                    });
                    ctx.container.appendChild(value);
                    return;
                }

                // Check field visibility
                if (fieldDef.visible && ctx.model && !fieldDef.visible(ctx.model)) {
                    ctx.container.style.display = 'none';
                }

                // Render label
                const label = createElement('div', {
                    className: 'ev-field-label',
                    textContent: fieldDef.label,
                });
                ctx.container.appendChild(label);

                // Render value using registry
                const result = renderViewField(fieldDef, ctx.value, ctx.model);
                ctx.container.appendChild(result.element);

                // Store for disposal
                if (result.dispose) {
                    this.viewFieldResults.set(ctx.key, result);
                }

                return result.dispose ? { dispose: result.dispose } : undefined;
            },
        });

        // Render layout with model
        if (this._model) {
            this.viewLayout.render(this._model);
        }
    }

    /**
     * Dispose view layout and field results.
     */
    private disposeViewLayout(): void {
        // Dispose field results
        for (const result of this.viewFieldResults.values()) {
            result.dispose?.();
        }
        this.viewFieldResults.clear();

        // Dispose layout
        if (this.viewLayout) {
            this.viewLayout.dispose();
            this.viewLayout = null;
        }
    }

    private renderFormMode(): void {
        // Dispose previous form layout
        this.disposeFormLayout();

        // Header with Save/Cancel buttons (unless hidden)
        if (!this.config.hideFormHeader) {
            const header = this.renderFormHeader();
            this.rootElement.appendChild(header);
        } else {
            // Still set up keyboard handler for Ctrl+Enter
            this.setupFormKeyboardHandler();
        }

        // Get form config (newForm or form)
        const formConfig = this.getFormConfig();

        // Build field lookup map from config
        const fieldMap = new Map<string, FormFieldDef<T>>();
        for (const field of formConfig.fields) {
            fieldMap.set(String(field.key), field);
        }

        // Create EntityLayout with form field renderer
        this.formLayout = new EntityLayout<T>({
            container: this.rootElement,
            layout: formConfig.layout,
            ariaLabel: this.config.ariaLabel,
            renderField: (ctx: FieldRenderContext<T>) => {
                const fieldDef = fieldMap.get(ctx.key);
                if (!fieldDef) {
                    // Field not defined - render placeholder
                    const label = createElement('div', {
                        className: 'ev-field-label',
                        textContent: ctx.key,
                    });
                    ctx.container.appendChild(label);

                    const input = createElement('input', {
                        className: 'ev-field-input ev-field-input--unknown',
                        attributes: { type: 'text', disabled: 'true' },
                    });
                    ctx.container.appendChild(input);
                    return;
                }

                // Check field visibility (hide container but still render content
                // so reevaluateVisibility can show it later)
                if (fieldDef.visible && ctx.model && !fieldDef.visible(ctx.model)) {
                    ctx.container.style.display = 'none';
                }

                // Render label with required indicator
                const labelWrapper = createElement('div', {
                    className: 'ev-field-label-wrapper',
                });

                const label = createElement('label', {
                    className: 'ev-field-label',
                    textContent: fieldDef.label,
                    attributes: { for: `ev-field-${ctx.key}` },
                });
                labelWrapper.appendChild(label);

                if (fieldDef.required) {
                    const required = createElement('span', {
                        className: 'ev-field-required',
                        textContent: '*',
                        attributes: { 'aria-label': 'required' },
                    });
                    labelWrapper.appendChild(required);
                }

                ctx.container.appendChild(labelWrapper);

                // Render form field using registry
                const result = renderFormField(
                    fieldDef,
                    ctx.value,
                    (newValue: unknown) => {
                        // Update model when field changes
                        this.setFieldValue(ctx.key, newValue);
                    }
                );

                // Add id for label association
                const inputEl = result.element.querySelector('input, textarea, select');
                if (inputEl) {
                    inputEl.id = `ev-field-${ctx.key}`;
                }

                ctx.container.appendChild(result.element);

                // Store result for value access and disposal
                this.formFieldResults.set(ctx.key, result);

                return { dispose: () => result.dispose() };
            },
        });

        // Render layout with model
        if (this._model) {
            this.formLayout.render(this._model);
        }
    }

    /**
     * Dispose form layout and field results.
     */
    private disposeFormLayout(): void {
        // Dispose field results
        for (const result of this.formFieldResults.values()) {
            result.dispose();
        }
        this.formFieldResults.clear();

        // Dispose layout
        if (this.formLayout) {
            this.formLayout.dispose();
            this.formLayout = null;
        }
    }

    private renderFormHeader(): HTMLElement {
        const header = createElement('div', {
            className: 'ev-form-header',
        });

        const actions = createElement('div', {
            className: 'ev-form-actions',
        });

        const cancelBtn = createElement('button', {
            className: 'cs-btn',
            textContent: 'Cancel',
            attributes: {
                type: 'button',
                title: 'Cancel (Escape)',
            },
            onClick: () => this.cancel(),
        });
        actions.appendChild(cancelBtn);

        const saveLabel = this._mode === 'new' ? 'Create' : 'Save';
        const saveBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: saveLabel,
            attributes: {
                type: 'button',
                title: `${saveLabel} (Ctrl+Enter)`,
            },
            onClick: () => this.save(),
        });
        actions.appendChild(saveBtn);

        // Add delete button in edit mode (not new mode) when onDelete is provided
        if (this._mode === 'edit' && this.config.onDelete) {
            const deleteBtn = createElement('button', {
                className: 'cs-btn cs-btn--no',
                textContent: 'Delete',
                attributes: {
                    type: 'button',
                    title: 'Delete',
                },
                onClick: async () => {
                    try {
                        await this.config.onDelete!();
                    } catch (error) {
                        console.error('Delete failed:', error);
                    }
                },
            });
            actions.appendChild(deleteBtn);
        }

        // Store reference for state updates
        this.saveButton = saveBtn;

        // Update initial button state
        this.updateSaveButtonState();

        header.appendChild(actions);

        // Add keyboard handler for the form
        this.setupFormKeyboardHandler();

        return header;
    }

    /** Reference to Save button for state updates */
    private saveButton: HTMLButtonElement | null = null;

    /**
     * Update the Save button disabled state based on form state.
     */
    private updateSaveButtonState(): void {
        if (!this.saveButton) return;

        // In new mode, always enable (no dirty check needed)
        // In edit mode, disable if not dirty
        const isNewMode = this._mode === 'new';
        const canSave = isNewMode || this.isDirty;

        this.saveButton.disabled = !canSave;
        this.saveButton.classList.toggle('cs-btn--disabled', !canSave);
    }

    /**
     * Setup keyboard shortcuts for the form.
     */
    private setupFormKeyboardHandler(): void {
        const handler = (e: KeyboardEvent) => {
            // Escape to cancel
            if (e.key === 'Escape') {
                e.preventDefault();
                this.cancel();
                return;
            }

            // Ctrl+Enter or Cmd+Enter to save
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                this.save();
                return;
            }
        };

        this.rootElement.addEventListener('keydown', handler);
        this.disposables.add(() => {
            this.rootElement.removeEventListener('keydown', handler);
        });
    }

    private renderLoadingState(): void {
        clearElement(this.rootElement);

        const loading = createElement('div', {
            className: 'ev-loading',
        });

        const spinner = createElement('div', {
            className: 'ev-spinner',
        });
        loading.appendChild(spinner);

        const text = createElement('div', {
            className: 'ev-loading-text',
            textContent: 'Loading...',
        });
        loading.appendChild(text);

        this.rootElement.appendChild(loading);
    }

    private renderErrorState(): void {
        clearElement(this.rootElement);

        const error = createElement('div', {
            className: 'ev-error',
        });

        const icon = createElement('div', {
            className: 'ev-error-icon',
            textContent: '!',
        });
        error.appendChild(icon);

        const message = createElement('div', {
            className: 'ev-error-message',
            textContent: this._loadError?.message ?? 'Failed to load',
        });
        error.appendChild(message);

        const retryBtn = createElement('button', {
            className: 'cs-btn cs-btn--secondary',
            textContent: 'Retry',
            onClick: () => this.loadModel(),
        });
        error.appendChild(retryBtn);

        this.rootElement.appendChild(error);
    }

    private renderErrors(): void {
        // Remove existing error displays
        const existingErrors = this.rootElement.querySelectorAll('.ev-field-error, .ev-form-error');
        existingErrors.forEach(el => el.remove());

        // Show form-level error if present
        const formError = this._errors.get('_form');
        if (formError) {
            const errorEl = createElement('div', {
                className: 'ev-form-error',
                textContent: formError,
            });
            this.rootElement.prepend(errorEl);
        }

        // Show field-level errors
        for (const [key, errorMessage] of this._errors) {
            if (key === '_form') continue;

            // Find the field container by data-field-key attribute (set by EntityLayout)
            const fieldContainer = this.rootElement.querySelector(`[data-field-key="${key}"]`);
            if (fieldContainer) {
                const errorEl = createElement('div', {
                    className: 'ev-field-error',
                    textContent: errorMessage,
                });
                fieldContainer.appendChild(errorEl);
            }
        }
    }

    // =========================================================================
    // Private: Validation
    // =========================================================================

    private async validateField(field: FormFieldDef<T>): Promise<void> {
        const key = String(field.key);
        const value = this.getFieldValue(key);

        // Check required
        if (field.required && (value === undefined || value === null || value === '')) {
            this._errors.set(key, `${field.label} is required`);
            return;
        }

        // Check custom validator on field definition
        if ('validate' in field && field.validate) {
            const result = field.validate(value as never, this._model!);
            if (result !== true) {
                this._errors.set(key, typeof result === 'string' ? result : `${field.label} is invalid`);
                return;
            }
        }

        // Check custom field controller validator (legacy)
        const controller = this._fieldControllers.get(key);
        if (controller?.validate) {
            const result = await controller.validate();
            if (result !== true) {
                this._errors.set(key, typeof result === 'string' ? result : `${field.label} is invalid`);
                return;
            }
        }

        // Check form field result validator (for custom fields)
        const formResult = this.formFieldResults.get(key);
        if (formResult?.validate) {
            const result = await formResult.validate();
            if (result !== true) {
                this._errors.set(key, typeof result === 'string' ? result : `${field.label} is invalid`);
            }
        }
    }

    // =========================================================================
    // Private: Helpers
    // =========================================================================

    private getFormConfig(): FormConfig<T> {
        if (this._mode === 'new' && this.config.newForm) {
            return this.config.newForm;
        }
        return this.config.form ?? { fields: [], layout: [] };
    }

    private async confirmDirtyState(): Promise<boolean> {
        if (this.config.confirmDiscard) return this.config.confirmDiscard();
        // No host-supplied dialog (standalone use): native fallback.
        return window.confirm('You have unsaved changes. Discard them?');
    }

    private focusFirstField(): void {
        const firstInput = this.rootElement.querySelector<HTMLElement>(
            'input, textarea, select, [tabindex="0"]'
        );
        firstInput?.focus();
    }

    private focusFirstError(): void {
        const firstErrorKey = this._errors.keys().next().value;
        if (!firstErrorKey) return;

        // Find the error element and scroll it into view
        if (firstErrorKey === '_form') {
            // Form-level error - scroll to top
            const formError = this.rootElement.querySelector('.ev-form-error');
            formError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            // Field-level error - scroll to the field
            const fieldContainer = this.rootElement.querySelector(`[data-field-key="${firstErrorKey}"]`);
            const errorEl = fieldContainer?.querySelector('.ev-field-error');
            if (errorEl) {
                errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            // Try to focus the field input
            const formResult = this.formFieldResults.get(firstErrorKey);
            if (formResult) {
                formResult.focus();
                return;
            }
            // Fall back to custom field controllers
            const controller = this._fieldControllers.get(firstErrorKey);
            controller?.focus?.();
        }
    }

    private cloneModel(model: T): T {
        return this.deepClone(model) as T;
    }

    private isModelEqual(a: T, b: T): boolean {
        return this.deepEqual(a, b);
    }

    /**
     * Deep clone a value, handling Maps and other non-JSON types.
     */
    private deepClone(value: unknown): unknown {
        if (value === null || typeof value !== 'object') {
            return value;
        }

        if (value instanceof Map) {
            const cloned = new Map();
            for (const [k, v] of value) {
                cloned.set(k, this.deepClone(v));
            }
            return cloned;
        }

        if (value instanceof Set) {
            const cloned = new Set();
            for (const v of value) {
                cloned.add(this.deepClone(v));
            }
            return cloned;
        }

        if (value instanceof Date) {
            return new Date(value.getTime());
        }

        if (Array.isArray(value)) {
            return value.map(v => this.deepClone(v));
        }

        // Plain object
        const cloned: Record<string, unknown> = {};
        for (const key of Object.keys(value as Record<string, unknown>)) {
            cloned[key] = this.deepClone((value as Record<string, unknown>)[key]);
        }
        return cloned;
    }

    /**
     * Deep equality check, handling Maps and other non-JSON types.
     */
    private deepEqual(a: unknown, b: unknown): boolean {
        if (a === b) return true;
        if (a === null || b === null) return false;
        if (typeof a !== typeof b) return false;
        if (typeof a !== 'object') return false;

        // Handle Maps
        if (a instanceof Map && b instanceof Map) {
            if (a.size !== b.size) return false;
            for (const [key, value] of a) {
                if (!b.has(key) || !this.deepEqual(value, b.get(key))) {
                    return false;
                }
            }
            return true;
        }
        if (a instanceof Map || b instanceof Map) return false;

        // Handle Sets
        if (a instanceof Set && b instanceof Set) {
            if (a.size !== b.size) return false;
            for (const value of a) {
                if (!b.has(value)) return false;
            }
            return true;
        }
        if (a instanceof Set || b instanceof Set) return false;

        // Handle Dates
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() === b.getTime();
        }
        if (a instanceof Date || b instanceof Date) return false;

        // Handle Arrays
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
                if (!this.deepEqual(a[i], b[i])) return false;
            }
            return true;
        }
        if (Array.isArray(a) || Array.isArray(b)) return false;

        // Plain objects
        const aObj = a as Record<string, unknown>;
        const bObj = b as Record<string, unknown>;
        const aKeys = Object.keys(aObj);
        const bKeys = Object.keys(bObj);

        if (aKeys.length !== bKeys.length) return false;

        for (const key of aKeys) {
            if (!Object.prototype.hasOwnProperty.call(bObj, key)) return false;
            if (!this.deepEqual(aObj[key], bObj[key])) return false;
        }

        return true;
    }
}
